import React, { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useUnscrollableBody, useWindowDimensions } from '../../../common/utils/windowDimensions';
import OfficeHeader from '../components/OfficeHeader/OfficeHeader';
import OfficeFooter from '../components/OfficeFooter/OfficeFooter';
import MenuOfficeMobile from '../../../widgets/menu/office/mobile/MenuOffice/MenuOffice';
import UserContext from '../../../common/utils/getContext';
import Language from '../../../common/decorators/Language';
import SeoData from '../../../widgets/seoData/SeoData';
import OfficeAlert from '../components/OfficeAlert';
import classNames from 'classnames';
import css from './office.module.scss';
import { useRouteAnimation } from '../../../common/utils/utils';
import { OfficeFontStyle } from '../../../project/uikit/fonts';

const Office = ({
    seoData,
    children,
    title,
    active,
    hideFooter,
    translates,
    showSidebar = false,
    customHeader = {},
    transparentFooter = true,
    headerRightButtons = {},
    canonical,
    titleBlock = {}
}) => {
    const { lang } = useContext(UserContext);

    const windowDimensions = useWindowDimensions();
    const [mainMenu, setMainMenu] = useState(false);

    const [widget, setWidget] = useState(false);
    // const [expandedBlock, setExpandedBlock] = useLocalStorage('expandedBlock', true);

    useUnscrollableBody((mainMenu || widget) && windowDimensions < 1300, 'Office');
    const widthDimensions = useWindowDimensions();

    useEffect(() => {
        if (widthDimensions < 1300) {
            setMainMenu(false);
        } else {
            setMainMenu(true);
        }
    }, [widthDimensions]);

    const screenWidth = useMemo(() => widthDimensions >= 768, [widthDimensions]);

    const toggleMenu = useCallback(() => {
        setMainMenu(!mainMenu);
    }, [setMainMenu, mainMenu]);

    const toggleWidget = useCallback(() => {
        setWidget((prev) => !prev);
    }, [setWidget]);

    const TitleBlock = () => (
        <div className={css.titleBlockWrapper}>
            {titleBlock?.component ? titleBlock?.component : null}
            <div className={classNames(css.newTitleBlock, titleBlock?.component && css.newTitleBlockPad)}>
                {titleBlock?.title ? (
                    <h1 id='titleName' className={classNames(css.newTitle)}>
                        {titleBlock?.title}
                    </h1>
                ) : null}
                {titleBlock?.text ? (
                    <p id='subtitleName' className={classNames(css.newUndertitle)}>
                        {titleBlock?.text}
                    </p>
                ) : null}
                {titleBlock?.breadcrumbs && Array.isArray(titleBlock?.breadcrumbs) ? (
                    <div id='titleBreadcrumbs' className={css.titleBreadcrumbs}>
                        {titleBlock.breadcrumbs.map((item, idx) => {
                            const isLast = !titleBlock.breadcrumbs[idx + 1];
                            return (
                                <>
                                    <a
                                        href={item.link}
                                        className={classNames(css.titleBlockBreadCrumb, 'grow', {
                                            [css.titleBlockBreadCrumbLast]: isLast
                                        })}
                                        key={item.link}
                                    >
                                        {item.title}
                                    </a>
                                    {!isLast ? <div className={css.titleBreadcrumbDot}></div> : null}
                                </>
                            );
                        })}
                    </div>
                ) : null}
                {titleBlock?.componentBottom}
            </div>
        </div>
    );

    const routerLoaderClass = useRouteAnimation();
    return (
        <Language translates={translates} page={'/office'}>
            <OfficeFontStyle />
            <div {...{ 'data-testid': 'loaded' }} className={classNames('officePage', css.officeWrapper)}>
                <OfficeAlert />
                <SeoData canonical={canonical} data={seoData} title={title} />

                {screenWidth ? null : ( // <MenuOffice active={active} mainMenu={mainMenu || windowDimensions > 1300} toggleMenu={toggleMenu} />
                    <MenuOfficeMobile active={active} mainMenu={mainMenu || windowDimensions > 1300} toggleMenu={toggleMenu} />
                )}

                {/* <SlideUserSidebar expanded={widget} removeMenu={() => toggleWidget()}>
                    {getUserWidget}
                </SlideUserSidebar> */}
                <div className={css.officeBlocks}>
                    <div className={css.officeContent}>
                        <div className={css.officeBgVideoWrapper}>
                            <div className={css.officeBgVideoRectangle}></div>
                            <video
                                playsInline
                                disablePictureInPicture
                                autoPlay
                                muted
                                className={classNames(css.officeBgVideo, 'hideControls')}
                                controls={false}
                                loop
                                preload='none'
                            >
                                <source src='/src/video/office/bg_video.webm' type='video/webm' />
                                <source src='/src/video/office/bg_video.mp4' type='video/mp4' />
                            </video>
                        </div>
                        <div className={css.officeContentWrapper}>
                            <div className={css.officeContentMine}>
                                <OfficeHeader
                                    showSidebar={showSidebar}
                                    toggleMenu={toggleMenu}
                                    mainMenu={mainMenu || windowDimensions > 1300}
                                    toggleWidget={toggleWidget}
                                    title={title}
                                    lang={lang}
                                    widget={widget}
                                    headerRightButtons={headerRightButtons}
                                    customHeader={customHeader}
                                />
                                <main
                                    className={classNames(css.officeMine, routerLoaderClass, {
                                        [css.officeMinePb0]: !transparentFooter || transparentFooter === 'hidden'
                                    })}
                                >
                                    {/* <Skeleton
                                        itemsShiners={true}
                                        pattern={breadcrumbsSkeleton}
                                        Component={BreadcrumbsBlock}
                                        componentProps={{
                                            flexColumn: breadcrumbsColumn
                                        }}
                                    /> */}
                                    {Object.values(titleBlock || {})?.length ? (
                                        <TitleBlock />
                                    ) : (
                                        <span className={css.officeWhiteSpace}></span>
                                    )}
                                    <div className={css.officeChildrenWrapper}>{children}</div>
                                </main>

                                {!hideFooter && <OfficeFooter transparentFooter={transparentFooter} />}
                                {/* {!screenWidth && <BtmMenuOffice active={active} />} */}
                            </div>

                            {/* {showSidebar && screenWidth ? getUserSidebar : null} */}
                        </div>
                    </div>
                </div>
            </div>
        </Language>
    );
};
Office.propTypes = {
    title: PropTypes.string,
    breadcrumbs: PropTypes.array,
    active: PropTypes.string,
    translates: PropTypes.object,
    hideFooter: PropTypes.bool,
    showSidebar: PropTypes.bool
};

export default React.memo(Office);
